import React, { useState, useEffect } from 'react';
import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Button } from '../Components/UI/Buttons';
import { Title, Subtitle, Text } from '../Components/UI/Typography';
import { Card, CardTitle, CardContent, ActionCard } from '../Components/UI/Cards';
import { Container, Header, FlexContainer, Grid } from '../Components/UI/Containers';
import { getAllUsers } from '../Services/Api/userService';
import { getAllSites } from '../Services/Api/siteService';
import { getAttendanceByDate, checkIn, checkOut } from '../Services/Api/attendanceService';
import { useAuth } from '../Context/AuthContext';
import AttendanceModal from '../Components/Attendance/Attendancecomponents/AttendanceModal';
import {
  FiUsers,
  FiMapPin,
  FiClock,
  FiEye,
  FiSettings,
  FiCheckCircle,
  FiLogOut
} from 'react-icons/fi';
import { usePopup } from '../Popups/PopupContext';
// Helper to get today's date string
const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to calculate duration between two times (format: "HH:mm:ss")
function getDuration(start, end) {
  if (!start || !end) return '--:--:--';
  const [sh, sm, ss] = start.split(':').map(Number);
  const [eh, em, es] = end.split(':').map(Number);
  const startDate = new Date(0, 0, 0, sh, sm, ss);
  const endDate = new Date(0, 0, 0, eh, em, es);
  let diff = (endDate - startDate) / 1000; // seconds
  if (diff < 0) diff += 24 * 3600; // handle overnight
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = Math.floor(diff % 60);
  return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
}

// Helper to get current time as "HH:mm:ss"
function getCurrentTimeString() {
  const now = new Date();
  return [
    String(now.getHours()).padStart(2, '0'),
    String(now.getMinutes()).padStart(2, '0'),
    String(now.getSeconds()).padStart(2, '0')
  ].join(':');
}

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = usePopup(); // Use global popup

  // State management
  const [userCount, setUserCount] = useState(0);
  const [siteCount, setSiteCount] = useState(0);
  const [attendanceSummary, setAttendanceSummary] = useState({ checkedIn: 0, checkedOut: 0, total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  const [userAttendanceData, setUserAttendanceData] = useState(null);
  const [now, setNow] = useState(getCurrentTimeString());

  // Fetch dashboard data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const today = getTodayDateString();

        // Use companyId from user context for getAllUsers
        const companyId = user?.company?.id || user?.company_id;
        const [users, sites, attendance] = await Promise.all([
          companyId ? getAllUsers(companyId).catch(() => []) : [],
          getAllSites().catch(() => []),
          getAttendanceByDate(today).catch(() => [])
        ]);

        setUserCount(users?.length || 0);
        setSiteCount(sites?.length || 0);

        const checkedIn = attendance?.filter(a => a.in_time && !a.out_time).length || 0;
        const checkedOut = attendance?.filter(a => a.out_time).length || 0;
        setAttendanceSummary({ checkedIn, checkedOut, total: attendance?.length || 0 });

        // Find current user's attendance data
        if (user?.username) {
          const userAttendance = attendance?.find(a => a.username === user.username);
          setUserAttendanceData(userAttendance || null);
        }

      } catch (err) {
        setError(err.message || 'Failed to load dashboard data.');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  // Refresh attendance data
  const refreshAttendanceData = async () => {
    try {
      const today = getTodayDateString();
      const attendance = await getAttendanceByDate(today);

      const checkedIn = attendance?.filter(a => a.in_time && !a.out_time).length || 0;
      const checkedOut = attendance?.filter(a => a.out_time).length || 0;
      setAttendanceSummary({ checkedIn, checkedOut, total: attendance?.length || 0 });

      // Update user's attendance data
      if (user?.username) {
        const userAttendance = attendance?.find(a => a.username === user.username);
        setUserAttendanceData(userAttendance || null);
      }
    } catch (error) {
      // ignore
    }
  };
  // Handle check-in and check-out
  const handleCheckIn = async (locationType, location, siteInfo, meetingInfo) => {
    try {
      if (!location?.coords?.latitude || !location?.coords?.longitude) {
        showError('Location Error', 'Invalid location data. Please ensure location is properly detected.');
        return;
      }
      
      const checkInData = {
        date: getTodayDateString(),
        location_type: locationType,
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        address: location.address || `${location.coords.latitude.toFixed(4)}, ${location.coords.longitude.toFixed(4)}`,
        site: locationType === 'Site' ? siteInfo : null,
        meeting_location: locationType === 'Meeting' ? meetingInfo : null
      };
      
      const response = await checkIn(checkInData);
      setUserAttendanceData(response);
      setIsAttendanceModalOpen(false);
      await refreshAttendanceData();
      
      showSuccess(
        'Check-in Successful!',
        `You have successfully checked in at ${locationType === 'Site' ? siteInfo?.name || 'Site' : 'Meeting Location'}.`
      );
    
    } catch (error) {
      showError('Check-in Failed', error.message || 'Please try again.');
    }
  };

  const handleCheckOut = async (locationType, location, siteInfo, meetingInfo) => {
    try {
      if (!location?.coords?.latitude || !location?.coords?.longitude) {
        showError('Location Error', 'Invalid location data. Please ensure location is properly detected.');
        return;
      }
      
      const checkOutData = {
        date: getTodayDateString(),
        location_type: locationType,
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        address: location.address || `${location.coords.latitude.toFixed(4)}, ${location.coords.longitude.toFixed(4)}`,
        site: locationType === 'Site' ? siteInfo : null,
        meeting_location: locationType === 'Meeting' ? meetingInfo : null
      };
      
      const response = await checkOut(checkOutData);
      setUserAttendanceData(response);
      setIsAttendanceModalOpen(false);
      await refreshAttendanceData();
      
      showSuccess(
        'Check-out Successful!',
        `You have successfully checked out from ${locationType === 'Site' ? siteInfo?.name || 'Site' : 'Meeting Location'}.`
      );
    } catch (error) {
      showError('Check-out Failed', error.message || 'Please try again.');
    }
  };

  // Update 'now' every second if user is checked in and not checked out
  useEffect(() => {
    let interval;
    if (userAttendanceData?.in_time && !userAttendanceData?.out_time) {
      interval = setInterval(() => {
        setNow(getCurrentTimeString());
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [userAttendanceData?.in_time, userAttendanceData?.out_time]);

  // Calculate duration for "My Attendance Today"
  const myDuration = (() => {
    if (!userAttendanceData?.in_time) return '--:--:--';
    if (userAttendanceData?.out_time) {
      return getDuration(userAttendanceData.in_time, userAttendanceData.out_time);
    }
    return getDuration(userAttendanceData.in_time, now);
  })();

  const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username : 'User';
  const userRole = user?.role || '';
  
  return (
    <Container fullHeight style={{ background: theme.colors.background,padding:0,}}>
      <Header style={{ paddingTop: 0}}>
        <FlexContainer justify="space-between" align="center" responsive>
          <div>
            <Title style={{ margin: 0, letterSpacing: '0.5px' }}>Dashboard</Title>
            <Subtitle style={{ margin: 0, color: theme.colors.text.secondary }}>
              Welcome back, <span style={{ color: theme.colors.primary }}>{userName}</span>
            </Subtitle>
          </div>
          <Button
            onClick={() => setIsAttendanceModalOpen(true)}
            variant="primary"
            style={{
              borderRadius: 24,
              fontWeight: 600,
              boxShadow: theme.shadows.medium,
              padding: '0.6rem 1.5rem',
              fontSize: '1rem'
            }}
          >
            <FlexContainer align="center" gap="xs">
              <FiClock size={18} />
              <span>Attendance</span>
            </FlexContainer>
          </Button>
        </FlexContainer>
      </Header>

      {isLoading ? (
        <FlexContainer justify="center" align="center" style={{ minHeight: '200px' }}>
          <Text size="lg" weight="semibold" style={{ color: theme.colors.text.secondary }}>Loading dashboard data...</Text>
        </FlexContainer>
      ) : error ? (
        <FlexContainer justify="center" align="center" style={{ minHeight: '200px' }}>
          <Text size="lg" weight="semibold" style={{ color: theme.colors.error || '#f44336' }}>Error: {error}</Text>
        </FlexContainer>
      ) : (
        <>
          {/* Summary Cards Grid */}
          <Grid
            columns={userAttendanceData ? 4 : 3}
            gap={theme.spacing.lg}
            mobileMinWidth="100%"
            minWidth="240px"
            style={{ marginBottom: theme.spacing.xl }}
          >
            {/* Users Card */}
            <Card style={{ borderRadius: 18, boxShadow: theme.shadows.medium }}>
              <CardTitle >
                <FlexContainer align="center" gap="xs">
                  <FiUsers size={22} color={theme.colors.primary} />
                  <span>Users</span>
                </FlexContainer>
              </CardTitle>
              <CardContent>
                <FlexContainer direction="column" align="center" gap="xs">
                  <Text
                    size="xxxl"
                    weight="bold"
                    style={{
                      margin: 0,
                      color: theme.colors.primary,
                      fontSize: '2.5rem'
                    }}
                  >
                    {userCount}
                  </Text>
                  <Text
                    size="sm"
                    style={{
                      margin: 10,
                      color: theme.colors.text.secondary
                    }}
                  >
                    Registered Users
                  </Text>
                </FlexContainer>
              </CardContent>
            </Card>

            {/* Sites Card */}
            <Card style={{ borderRadius: 18, boxShadow: theme.shadows.medium }}>
              <CardTitle>
                <FlexContainer align="center" gap="xs">
                  <FiMapPin size={22} color={theme.colors.primary} />
                  <span>Sites</span>
                </FlexContainer>
              </CardTitle>
              <CardContent>
                <FlexContainer direction="column" align="center" gap="xs">
                  <Text
                    size="xxxl"
                    weight="bold"
                    style={{
                      margin: 0,
                      color: theme.colors.primary,
                      fontSize: '2.5rem'
                    }}
                  >
                    {siteCount}
                  </Text>
                  <Text
                    size="sm"
                    style={{
                      margin: 0,
                      color: theme.colors.text.secondary
                    }}
                  >
                    Registered Sites
                  </Text>
                </FlexContainer>
              </CardContent>
            </Card>

            {/* Attendance Summary Card */}
            <Card style={{ borderRadius: 18, boxShadow: theme.shadows.medium }}>
              <CardTitle>
                <FlexContainer align="center" gap="xs">
                  <FiClock size={22} color={theme.colors.primary} />
                  <span>Today's Attendance</span>
                </FlexContainer>
              </CardTitle>
              <CardContent>
                <FlexContainer direction="column" gap="sm">
                  <FlexContainer justify="space-between" align="center">
                    <Text style={{ margin: 0 }}>Currently Checked In</Text>
                    <Text style={{ margin: 0 }}>
                      <span style={{
                        fontWeight: theme.typography.fontWeights.bold,
                        color: theme.colors.primary
                      }}>
                        {attendanceSummary.checkedIn}
                      </span>
                    </Text>
                  </FlexContainer>
                  <FlexContainer justify="space-between" align="center">
                    <Text style={{ margin: 0 }}>Checked Out Today</Text>
                    <Text style={{ margin: 0 }}>
                      <span style={{
                        fontWeight: theme.typography.fontWeights.bold,
                        color: theme.colors.success || '#4CAF50'
                      }}>
                        {attendanceSummary.checkedOut}
                      </span>
                    </Text>
                  </FlexContainer>
                  <FlexContainer justify="space-between" align="center">
                    <Text style={{ margin: 0 }}>Total Records Today</Text>
                    <Text style={{ margin: 0 }}>
                      <span style={{ fontWeight: theme.typography.fontWeights.bold }}>
                        {attendanceSummary.total}
                      </span>
                    </Text>
                  </FlexContainer>
                </FlexContainer>
              </CardContent>
            </Card>

            {/* My Attendance Today Card */}
            {userAttendanceData && (
              <Card style={{ borderRadius: 18, boxShadow: theme.shadows.medium, background: theme.colors.surface }}>
                <CardTitle>
                  <FlexContainer align="center" gap="xs">
                    <FiCheckCircle size={22} color={theme.colors.success || "#4CAF50"} />
                    <span>My Attendance Today</span>
                  </FlexContainer>
                </CardTitle>
                <CardContent>
                  <FlexContainer direction="column" gap="sm">
                    <FlexContainer align="center" gap="md">
                      <Text size="sm" weight="semibold" style={{ color: theme.colors.text.secondary }}>
                        Check-in:
                      </Text>
                      <Text size="lg" weight="semibold" style={{ color: theme.colors.primary }}>
                        {userAttendanceData.in_time || 'Not checked in'}
                      </Text>
                      {userAttendanceData.in_time && (
                        <FiCheckCircle size={18} color={theme.colors.primary} />
                      )}
                    </FlexContainer>
                    
                    <FlexContainer align="center" gap="md">
                      <Text size="sm" weight="semibold" style={{ color: theme.colors.text.secondary }}>
                        Check-out:
                      </Text>
                      <Text size="lg" weight="semibold" style={{ color: theme.colors.success || "#4CAF50" }}>
                        {userAttendanceData.out_time || 'Not checked out'}
                      </Text>
                      {userAttendanceData.out_time && (
                        <FiLogOut size={18} color={theme.colors.success || "#4CAF50"} />
                      )}
                    </FlexContainer>
                    {userAttendanceData.out_location_type && (
                      <Text size="xs" style={{ color: theme.colors.text.secondary, marginLeft: 24 }}>
                        {userAttendanceData.out_location_type}
                      </Text>
                    )}
                    <FlexContainer align="center" gap="md">
                      <Text size="sm" weight="semibold" style={{ color: theme.colors.text.secondary }}>
                        Duration:
                      </Text>
                      <Text size="lg" weight="bold" style={{ color: theme.colors.primary }}>
                        {myDuration}
                      </Text>
                      {userAttendanceData.in_location_type && (
                      <Text size="xs" style={{ color: theme.colors.text.secondary, marginLeft: 24 }}>
                        {userAttendanceData.in_location_type}
                        {userAttendanceData.in_location && ` - ${userAttendanceData.in_location}`}
                      </Text>
                    )}
                    </FlexContainer>
                  </FlexContainer>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Admin Quick Links */}
          {user?.role === 'Admin' && (
            <>
              <Subtitle style={{ margin: '0 0 16px 0', color: theme.colors.text.secondary, fontWeight: 600 }}>Quick Actions</Subtitle>
              <Grid mobileMinWidth="100%" minWidth="200px" gap={theme.spacing.lg}>
                <ActionCard onClick={() => navigate('/attendance')} style={{ borderRadius: 16, boxShadow: theme.shadows.small }}>
                  <CardTitle>
                    <FlexContainer align="center" gap="xs">
                      <FiEye size={20} color={theme.colors.primary} />
                      <span>Attendance</span>
                    </FlexContainer>
                  </CardTitle>
                  <CardContent>
                    <Text
                      size="sm"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      View and manage daily attendance records.
                    </Text>
                  </CardContent>
                </ActionCard>

                <ActionCard onClick={() => navigate('/usermanagement')} style={{ borderRadius: 16, boxShadow: theme.shadows.small }}>
                  <CardTitle>
                    <FlexContainer align="center" gap="xs">
                      <FiUsers size={20} color={theme.colors.primary} />
                      <span>Users</span>
                    </FlexContainer>
                  </CardTitle>
                  <CardContent>
                    <Text
                      size="sm"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      Add, edit, or remove user accounts.
                    </Text>
                  </CardContent>
                </ActionCard>

                <ActionCard onClick={() => navigate('/sitemanagement/register')} style={{ borderRadius: 16, boxShadow: theme.shadows.small }}>
                  <CardTitle>
                    <FlexContainer align="center" gap="xs">
                      <FiSettings size={20} color={theme.colors.primary} />
                      <span>Sites</span>
                    </FlexContainer>
                  </CardTitle>
                  <CardContent>
                    <Text
                      size="sm"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      Define and manage site boundaries.
                    </Text>
                  </CardContent>
                </ActionCard>
              </Grid>
            </>
          )}
        </>
      )}

         {/* Attendance Modal */}
      <AttendanceModal
        isOpen={isAttendanceModalOpen}
        onClose={() => setIsAttendanceModalOpen(false)}
        onCheckIn={handleCheckIn}
        onCheckOut={handleCheckOut}
        attendanceData={userAttendanceData}
      />

    </Container>
  );
};

export default Dashboard;
